<script setup lang="ts">
import type { ComboboxRootEmits, ComboboxRootProps } from 'reka-ui';
import type { HTMLAttributes } from 'vue';

import { cn } from '@/utils';
import { ComboboxRoot, useForwardPropsEmits } from 'reka-ui';

const props = withDefaults(
	defineProps<ComboboxRootProps & { class?: HTMLAttributes['class'] }>(),
	{
		open: true,
		modelValue: ''
	}
);

const emits = defineEmits<ComboboxRootEmits>();

const delegatedProps = computed(() => {
	const { class: _, ...delegated } = props;

	return delegated;
});

const forwarded = useForwardPropsEmits(delegatedProps, emits);

/**
 * Handle click event on the ComboboxRoot
 * @param event - The click event
 *
 * This is a workaround to ensure that the combobox is open when clicking on a child input.
 * When navigating with the keyboard, after selecting an item with Enter, you cant click on the input
 * and open it again.
 */
function handleClick(event: MouseEvent) {
	const target = event.target;
	if (
		target instanceof HTMLElement &&
		(target.tagName === 'INPUT' || target.closest('input'))
	) {
		emits('update:open', true);
	}
}
</script>

<template>
	<ComboboxRoot
		v-bind="forwarded"
		:class="
			cn(
				'flex h-full w-full flex-col overflow-hidden rounded-md bg-popover text-popover-foreground',
				props.class
			)
		"
		@click="handleClick"
	>
		<slot />
	</ComboboxRoot>
</template>
